// src/components/home/DashboardCards.tsx — the director summary as the top of the home Dashboard (merged from the old
// Director page, 19 Oct 2026).
//
// Needs me, then four story cards (money in, money out, bank to reconcile, portfolio), then what could bite. Each card
// is a chart, a row of figures and one plain sentence saying what the number means. Every figure arrives from
// /director/summary already null when the viewer lacks the page behind it, so a card whose figures are all null is not
// drawn: what someone sees is decided by their permissions alone, never by their role.
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { NeedsMeQueue } from '../director/NeedsMeQueue';
import { DrillDrawer, FigureTile, Panel } from '../director';
import { Segmented } from '../ui';
import { anyFigure as any, fmtMoney, monthLabel, type DirectorSummary, type Drill, type Figure, type Period } from '../../lib/director';
import { GRID_COLOR, SEQUENTIAL, STATUS_VAR, TEXT_MUTED, formatMoneyCompact, seriesColor } from '../../lib/viz';

const PERIODS: Array<{ key: Period; label: string }> = [{ key: 'month', label: 'This month' }, { key: 'quarter', label: 'This quarter' }, { key: 'year', label: 'This year' }];

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

export function PeriodToggle({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  return <Segmented items={PERIODS} value={period} onChange={onChange} />;
}

type Tone = 'good' | 'warning' | 'critical';

/** A status always carries an icon and a word, never colour alone. */
function StatusChip({ tone, label }: { tone: Tone; label: string }) {
  const Icon = tone === 'good' ? CheckCircle2 : AlertTriangle;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink-50 px-2 py-0.5 text-[11px] font-semibold text-ink-700">
      <Icon className="h-3.5 w-3.5" style={{ color: STATUS_VAR[tone] }} aria-hidden /> {label}
    </span>
  );
}

/** One story: heading, subtitle, a chart, the figures behind it, and the sentence that says what it means. */
function StoryCard({ id, title, subtitle, status, chart, sentence, children }: {
  id: string; title: string; subtitle: string; status?: ReactNode; chart?: ReactNode; sentence: ReactNode; children?: ReactNode;
}) {
  // `relative` keeps the chart's screen-reader table (absolutely positioned) inside the card, not below the page.
  return (
    <section id={id} className="surface relative flex min-w-0 scroll-mt-6 flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[13px] font-semibold text-ink-900">{title}</h2>
          <p className="mt-0.5 text-[12px] text-ink-500">{subtitle}</p>
        </div>
        {status}
      </div>
      {chart}
      {children && <div className="grid grid-cols-2 gap-2.5">{children}</div>}
      <p className="mt-auto text-[13px] leading-relaxed text-ink-600">{sentence}</p>
    </section>
  );
}

// ── Charts ─────────────────────────────────────────────────────────────────

type Series = { key: string; label: string; colour: string };

function ChartTooltip({ active, payload, label, series, currency }: { active?: boolean; payload?: Array<{ dataKey?: unknown; value?: unknown }>; label?: unknown; series: Series[]; currency: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-[12px] shadow-pop">
      <div className="mb-1 font-semibold text-ink-900">{monthLabel(String(label))}</div>
      {payload.map((p) => {
        const s = series.find((x) => x.key === String(p.dataKey));
        return <div key={String(p.dataKey)} className="flex items-center gap-2 text-ink-600"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: s?.colour }} />{s?.label}<span className="ml-auto pl-3 font-medium text-ink-900">{fmtMoney(Number(p.value ?? 0), currency)}</span></div>;
      })}
    </div>
  );
}

/** Six months of columns. A legend for two series, a tooltip on hover, and a hidden table for screen readers. */
function MonthBars({ data, series, currency, caption }: { data: Array<Record<string, number | string>>; series: Series[]; currency: string; caption: string }) {
  if (!data.length) return null;
  return (
    <figure className="m-0">
      <div className="h-44 w-full" aria-hidden>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }} barGap={2} barCategoryGap="22%">
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="month" tickFormatter={(m) => monthLabel(String(m))} tick={{ fontSize: 11, fill: TEXT_MUTED }} axisLine={false} tickLine={false} />
            <YAxis width={46} tickFormatter={(v) => formatMoneyCompact(Number(v), currency)} tick={{ fontSize: 10.5, fill: TEXT_MUTED }} axisLine={false} tickLine={false} tickCount={4} />
            <Tooltip content={<ChartTooltip series={series} currency={currency} />} cursor={{ fill: 'var(--color-ink-100)' }} />
            {series.map((s) => <Bar key={s.key} dataKey={s.key} fill={s.colour} maxBarSize={24} radius={[4, 4, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {series.length > 1 && (
        <div className="mt-1.5 flex flex-wrap gap-3 text-[11.5px] text-ink-500">
          {series.map((s) => <span key={s.key} className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: s.colour }} />{s.label}</span>)}
        </div>
      )}
      <table className="sr-only">
        <caption>{caption}</caption>
        <thead><tr><th>Month</th>{series.map((s) => <th key={s.key}>{s.label}</th>)}</tr></thead>
        <tbody>{data.map((r) => <tr key={String(r.month)}><td>{monthLabel(String(r.month))}</td>{series.map((s) => <td key={s.key}>{fmtMoney(Number(r[s.key] ?? 0), currency)}</td>)}</tr>)}</tbody>
      </table>
    </figure>
  );
}

/** A meter for one percentage: the arc in the status colour, the number and its word in the middle. */
function Ring({ pct, tone, word, onClick }: { pct: number; tone: Tone; word: string; onClick: () => void }) {
  const r = 52; const c = 2 * Math.PI * r; const v = Math.max(0, Math.min(100, pct));
  return (
    <button type="button" onClick={onClick} aria-label={`Occupancy ${pct}%, ${word}. Open the flats behind it.`}
      className="group relative mx-auto grid h-40 w-40 shrink-0 place-items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-400">
      <svg viewBox="0 0 128 128" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
        <circle cx="64" cy="64" r={r} fill="none" stroke={GRID_COLOR} strokeWidth="12" />
        <circle cx="64" cy="64" r={r} fill="none" stroke={STATUS_VAR[tone]} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`} />
      </svg>
      <span className="relative text-center">
        <span className="block text-[22px] font-bold leading-none text-ink-900">{pct}%</span>
        <span className="mt-1 block text-[11px] font-medium text-ink-500 group-hover:text-bronze-700">{word}</span>
      </span>
    </button>
  );
}

// ── Bank ───────────────────────────────────────────────────────────────────

function BankCard({ bank, currency, go }: { bank: DirectorSummary['bank']; currency: string; go: (path: string) => void }) {
  const { unallocated: inn, suggested, unmatchedDebits: out } = bank;
  const lines = (inn?.count ?? 0) + (out?.count ?? 0);
  const total = (inn?.value ?? 0) + (out?.value ?? 0);
  const segs = [
    { key: 'in', fig: inn, label: 'Money in with nowhere to go', colour: seriesColor(0) },
    { key: 'out', fig: out, label: 'Money out still to match', colour: seriesColor(1) },
  ].filter((s): s is typeof s & { fig: Figure } => !!s.fig);
  const open = lines > 0;
  const path = (f: Figure) => f.drill.path ?? '/app/banking';

  return (
    <StoryCard id="dir-bank" title="Bank to reconcile" subtitle="Lines from the bank feed not yet matched, as of today"
      status={<StatusChip tone={open ? 'warning' : 'good'} label={open ? 'To do' : 'All clear'} />}
      sentence={open
        ? <>{fmtMoney(total, currency)} across {plural(lines, 'bank line')} still to reconcile{suggested?.value ? <> — {plural(suggested.value, 'credit')} already {suggested.value === 1 ? 'has' : 'have'} a suggested match</> : null}.</>
        : 'Nothing waiting — the bank is fully reconciled.'}
      chart={
        <div>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-[22px] font-bold leading-none text-ink-900">{fmtMoney(total, currency)}</span>
            <span className="text-[12.5px] text-ink-500">open across {plural(lines, 'line')}</span>
          </div>
          <div className="flex h-9 w-full gap-[2px] overflow-hidden rounded-[4px]">
            {total > 0 ? segs.filter((s) => s.fig.value > 0).map((s) => (
              <button key={s.key} type="button" onClick={() => go(path(s.fig))} title={`${s.label}: ${fmtMoney(s.fig.value, currency)}`}
                aria-label={`${s.label}: ${fmtMoney(s.fig.value, currency)}, ${plural(s.fig.count ?? 0, 'line')}. Open Banking.`}
                className="h-full min-w-[10px] transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                style={{ flexGrow: s.fig.value, flexBasis: 0, background: s.colour }} />
            )) : <div className="grid h-full w-full place-items-center bg-ink-50 text-[11.5px] text-ink-500">Every line matched</div>}
          </div>
          <ul className="mt-3 divide-y divide-ink-100">
            {segs.map((s) => (
              <li key={s.key}><button type="button" onClick={() => go(path(s.fig))} className="flex w-full items-center gap-3 rounded-lg px-1 py-2 text-left transition hover:bg-bronze-50/60">
                <i className="inline-block h-3 w-3 shrink-0 rounded-sm" style={{ background: s.colour }} aria-hidden />
                <span className="min-w-0 flex-1 text-[13px] leading-snug text-ink-700">{s.label}</span>
                <span className="text-[12px] text-ink-500">{plural(s.fig.count ?? 0, 'line')}</span>
                <span className="shrink-0 text-right text-[13.5px] font-semibold text-ink-900 sm:w-24">{fmtMoney(s.fig.value, currency)}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" aria-hidden />
              </button></li>
            ))}
            {suggested && (
              <li><button type="button" onClick={() => go(path(suggested))} className="flex w-full items-center gap-3 rounded-lg px-1 py-2 text-left transition hover:bg-bronze-50/60">
                <span className="grid h-5 min-w-[20px] shrink-0 place-items-center rounded-full bg-bronze-500 px-1.5 text-[11px] font-semibold text-on-primary">{suggested.value}</span>
                <span className="min-w-0 flex-1 text-[13px] leading-snug text-ink-700">Credits with a suggested match <span className="text-ink-400">· one click to allocate</span></span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" aria-hidden />
              </button></li>
            )}
          </ul>
        </div>
      } />
  );
}

// ── The page section ───────────────────────────────────────────────────────

export function DashboardCards({ summary: s, period, tab, highlightId }: { summary: DirectorSummary; period: Period; tab: string | null; highlightId: string | null }) {
  const navigate = useNavigate();
  const [drill, setDrill] = useState<{ drill: Drill; title: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A figure kept on its own page (no report behind it) goes straight there; the rest open their rows in a drawer.
  const onDrill = (d: Drill, title: string) => { if (!d.reportKey && d.path) navigate(d.path); else setDrill({ drill: d, title }); };

  // ?tab=money|risk|portfolio|bank brings that card into view; ?ai=<id> the queue item.
  useEffect(() => {
    const el = highlightId ? (document.getElementById(`needs-${highlightId}`) ?? document.getElementById('dir-needs')) : tab ? document.getElementById(`dir-${tab}`) : null;
    el?.scrollIntoView({ behavior: 'smooth', block: highlightId ? 'center' : 'start' });
  }, [tab, highlightId]);

  const c = s.currency;
  const pLabel = PERIODS.find((p) => p.key === period)!.label.toLowerCase();
  const { comingIn: i, goingOut: o, portfolio: p, couldBite: b } = s;
  const zeroGood = (v: number | undefined): Tone => (v ? 'critical' : 'good');

  const showIn = any(i.rentRollMinor, i.collectedPctMtd, i.feeIncomeMinor, i.otherIncome) || !!i.trend;
  const showOut = any(o.maintenanceSpend, o.contractsAnnual, o.agentFees, o.voidsCostMinor) || !!o.trend;
  const showBank = any(s.bank.unallocated, s.bank.unmatchedDebits);
  // No flats in view (units 0) makes occupancy a meaningless 0% — that figure is left out rather than drawn as a red ring.
  const noFlats = p.units?.value === 0;
  const showPortfolio = noFlats ? any(p.onMarket, p.changeoversInProgress, p.moveInsNext30, p.moveOutsNext30) : any(...Object.values(p));
  const showRisk = any(...Object.values(b));

  const collected = i.collectedPctMtd;
  const collectedTone: Tone | undefined = collected ? (collected.value >= 97 ? 'good' : collected.value >= 92 ? 'warning' : 'critical') : undefined;
  const occ = noFlats ? null : p.occupancyPct;
  const occTone: Tone = occ ? (occ.value >= 95 ? 'good' : occ.value >= 88 ? 'warning' : 'critical') : 'good';

  const inSentence = [
    collected ? `${collected.value}% of this month's rent is in${collected.count ? `; ${fmtMoney(collected.count, c)} still owed` : ' — nothing outstanding'}` : null,
    i.rentRollMinor ? `${fmtMoney(i.rentRollMinor.value, c)} a year in rent across ${plural(i.rentRollMinor.count ?? 0, 'live tenancy', 'live tenancies')}` : null,
  ].filter(Boolean).join('. ');
  const outSentence = [
    o.maintenanceSpend ? (o.maintenanceSpend.value ? `${fmtMoney(o.maintenanceSpend.value, c)} spent on works ${pLabel} across ${plural(o.maintenanceSpend.count ?? 0, 'finished job')}` : `Nothing spent on works ${pLabel}`) : null,
    o.voidsCostMinor?.value ? `empty flats cost ${fmtMoney(o.voidsCostMinor.value, c)} in lost rent` : null,
    !o.maintenanceSpend && o.contractsAnnual ? `${fmtMoney(o.contractsAnnual.value, c)} a year committed to ${plural(o.contractsAnnual.count ?? 0, 'supplier contract')}` : null,
  ].filter(Boolean).join('; ');
  const portSentence = occ && p.units
    ? `${occ.value}% of ${plural(p.units.value, 'flat')} are let; ${p.vacant?.value ?? 0} empty${p.onMarket ? `, ${p.onMarket.value} on the market` : ''}.`
    : p.onMarket ? `${plural(p.onMarket.value, 'flat')} on the market today.` : 'Turnaround and moves across the portfolio.';

  return (
    <div className="space-y-2.5">
      {notice && <div className="rounded-xl bg-emerald-50 px-4 py-2.5 text-[13px] text-emerald-800">{notice}</div>}
      {s.needsMe.total > 0 && <div id="dir-needs" className="scroll-mt-6"><NeedsMeQueue needs={s.needsMe} onNotice={setNotice} highlightId={highlightId} /></div>}

      {(showIn || showOut || showBank || showPortfolio) && (
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
          {showIn && (
            <StoryCard id="dir-money" title="Money in" subtitle={i.trend ? `Rent due and collected, last ${i.trend.length} months · ${c}` : `Rent and income · ${c}`}
              status={collectedTone && <StatusChip tone={collectedTone} label={collectedTone === 'good' ? 'On track' : collectedTone === 'warning' ? 'Behind' : 'Well behind'} />}
              chart={i.trend && <MonthBars data={i.trend} currency={c} caption="Rent due and collected by month"
                series={[{ key: 'raisedMinor', label: 'Rent due', colour: SEQUENTIAL[2] }, { key: 'collectedMinor', label: 'Collected', colour: seriesColor(0) }]} />}
              sentence={inSentence ? `${inSentence}.` : `Fee and other income ${pLabel}.`}>
              <FigureTile label="Rent roll a year" kind="money" figure={i.rentRollMinor} hint={i.rentRollMinor ? `${plural(i.rentRollMinor.count ?? 0, 'live tenancy', 'live tenancies')}` : undefined} onDrill={onDrill} />
              <FigureTile label="Collected this month" kind="percent" figure={collected} hint={collected ? `${fmtMoney(collected.count ?? 0, c)} outstanding` : undefined} status={collectedTone} onDrill={onDrill} />
              <FigureTile label={`Fee income ${pLabel}`} kind="money" figure={i.feeIncomeMinor} hint={i.feeIncomeMinor ? (i.feeIncomeMinor.basis === 'POSTED' ? 'management fee, posted' : 'management fee, partly estimated') : undefined} onDrill={onDrill} />
              <FigureTile label={`Other income ${pLabel}`} kind="money" figure={i.otherIncome} hint="posted to the ledger, not rent" onDrill={onDrill} />
            </StoryCard>
          )}

          {showOut && (
            <StoryCard id="dir-out" title="Money out" subtitle={o.trend ? `Works finished, last ${o.trend.length} months · ${c}` : `Contracts, agent fees and empty flats · ${c}`}
              chart={o.trend && <MonthBars data={o.trend} currency={c} caption="Works spend by month" series={[{ key: 'spendMinor', label: 'Works finished', colour: seriesColor(0) }]} />}
              sentence={outSentence ? `${outSentence.charAt(0).toUpperCase()}${outSentence.slice(1)}.` : `What the portfolio costs to run, ${pLabel}.`}>
              <FigureTile label={`Works ${pLabel}`} kind="money" figure={o.maintenanceSpend} hint={o.maintenanceSpend ? `${plural(o.maintenanceSpend.count ?? 0, 'job')} finished` : undefined} onDrill={onDrill} />
              <FigureTile label="Contracts a year" kind="money" figure={o.contractsAnnual} hint={o.contractsAnnual ? plural(o.contractsAnnual.count ?? 0, 'supplier contract') : undefined} onDrill={onDrill} />
              <FigureTile label={`Agent fees ${pLabel}`} kind="money" figure={o.agentFees} hint={o.agentFees ? plural(o.agentFees.count ?? 0, 'agent invoice') : undefined} onDrill={onDrill} />
              <FigureTile label="Empty and costing" kind="money" figure={o.voidsCostMinor} hint="rent lost on empty flats" status={o.voidsCostMinor?.value ? 'warning' : undefined} onDrill={onDrill} />
            </StoryCard>
          )}

          {showBank && <BankCard bank={s.bank} currency={c} go={navigate} />}

          {showPortfolio && (
            <StoryCard id="dir-portfolio" title="Portfolio" subtitle="Flats, letting and turnaround, as of today"
              status={occ && <StatusChip tone={occTone} label={occTone === 'good' ? 'Healthy' : occTone === 'warning' ? 'Watch' : 'Too many empty'} />}
              chart={occ && <Ring pct={occ.value} tone={occTone} word={occTone === 'good' ? 'let' : occTone === 'warning' ? 'let · watch' : 'let · low'} onClick={() => onDrill(occ.drill, 'Occupancy')} />}
              sentence={portSentence}>
              <FigureTile label="Occupancy" kind="percent" figure={occ} hint={p.units ? `${p.units.value} flats · ${p.vacant?.value ?? 0} empty` : undefined} status={occ ? occTone : undefined} onDrill={onDrill} />
              <FigureTile label="On the market" figure={p.onMarket} hint="flats being advertised" onDrill={onDrill} />
              <FigureTile label="Changeovers running" figure={p.changeoversInProgress} hint="flats being turned round" onDrill={onDrill} />
              <FigureTile label="Moving in / out · 30 days" figure={(p.moveInsNext30 || p.moveOutsNext30) && { ...(p.moveInsNext30 ?? p.moveOutsNext30)!, value: (p.moveInsNext30?.value ?? 0) + (p.moveOutsNext30?.value ?? 0) }}
                hint={p.moveInsNext30 || p.moveOutsNext30 ? `${p.moveInsNext30?.value ?? 0} in · ${p.moveOutsNext30?.value ?? 0} out` : undefined} onDrill={onDrill} />
            </StoryCard>
          )}
        </div>
      )}

      {showRisk && (
        <div id="dir-risk" className="scroll-mt-6">
          <Panel title="What could bite" subtitle="The things that turn into a fine, a claim or a bad month">
            <FigureTile label="Certificates out of date" figure={b.complianceOverdue} hint={b.complianceDueSoon ? `${b.complianceDueSoon.value} more due within 60 days` : undefined} status={zeroGood(b.complianceOverdue?.value)} onDrill={onDrill} />
            <FigureTile label="Arrears over a month" figure={b.arrearsOver31Days && { ...b.arrearsOver31Days, value: b.arrearsOver31Days.count ?? 0 }}
              hint={b.arrearsOver31Days ? `${fmtMoney(b.arrearsOver31Days.value, c)} owed` : undefined} status={zeroGood(b.arrearsOver31Days?.count)} onDrill={onDrill} />
            <FigureTile label="Deposits past the deadline" figure={b.depositsUnprotectedPastDue} hint="not protected within 30 days" status={zeroGood(b.depositsUnprotectedPastDue?.value)} onDrill={onDrill} />
            <FigureTile label="Advances outstanding" kind="money" figure={b.advancesOutstanding} hint={b.advancesOutstanding ? `${b.advancesOutstanding.count} owed to the group` : undefined} onDrill={onDrill} />
            <FigureTile label="Properties overdrawn" figure={b.overdrawnProperties} status={zeroGood(b.overdrawnProperties?.value)} onDrill={onDrill} />
            <FigureTile label="Section 20 to consult" figure={b.section20Pending} hint="qualifying contracts, no consultation" status={b.section20Pending?.value ? 'warning' : 'good'} onDrill={onDrill} />
            <FigureTile label="Contractor insurance" figure={b.insuranceExpiring} hint="expired or within 30 days" status={b.insuranceExpiring?.value ? 'warning' : 'good'} onDrill={onDrill} />
          </Panel>
        </div>
      )}

      {drill && <DrillDrawer drill={drill.drill} title={drill.title} onClose={() => setDrill(null)} />}
    </div>
  );
}
